import { useEffect, useMemo, useRef, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { API } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Users, BookOpen, MessageSquare, X, Save, Sparkles, Archive, Eye, EyeOff, Clock, Upload, ImageIcon, CalendarClock, GripVertical } from "lucide-react";
import useModalA11y from "@/lib/useModalA11y";
import { Palette, Type as TypeIcon, RotateCcw, Globe, Share2, LayoutGrid, Menu as MenuIcon, ChevronUp as ChevronUpIcon, ChevronDown as ChevronDownIcon } from "lucide-react";

const FONT_OPTS = ["", "Inter", "Poppins", "Montserrat", "Roboto", "Lora", "Playfair Display", "system-ui"];
const RADIUS_OPTS = [["", "Default"], ["0px", "Sharp"], ["6px", "Small"], ["8px", "Medium"], ["12px", "Large"], ["16px", "X-Large"], ["9999px", "Pill"]];
const THEME_FIELDS = [
  ["primary", "Primary / accent", "#f47a20"],
  ["primaryHover", "Accent hover", "#ff933f"],
  ["background", "Page background", "#000000"],
  ["surface", "Surface / cards", "#141414"],
  ["text", "Main text", "#FFFFFF"],
  ["textMuted", "Muted text", "#A1A1AA"],
  ["border", "Borders", "rgba(255,255,255,0.08)"],
];

const emptySettings = () => ({
  brand: { clinicName: "", tagline: "", logoUrl: "", logoLightUrl: "", logoMarkUrl: "", faviconUrl: "", shareImageUrl: "" },
  theme: { primary: "", primaryHover: "", background: "", surface: "", text: "", textMuted: "", border: "" },
  themeLight: { primary: "", primaryHover: "", background: "", surface: "", text: "", textMuted: "", border: "" },
  typography: { headingFont: "", bodyFont: "", radius: "" },
  social: { instagram: "", facebook: "", linkedin: "", youtube: "", tiktok: "" },
  seo: { title: "", description: "", shareTitle: "", shareDescription: "" },
  nav: { items: [], signIn: { label: "", visible: true }, book: { label: "", path: "", visible: true } },
  pages: {},
});

const NAV_KEYS = [
  ["home", "Home"], ["team", "The Team"], ["services", "Services"], ["prices", "Prices"],
  ["finder", "Find Your Service"], ["resources", "Resources"], ["community", "Community"],
  ["testimonials", "Testimonials"], ["contact", "Contact"],
];

const isHex = (v) => !v || /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$|^rgba?\([0-9.,%\s]+\)$/.test(v.trim());

function Field({ label, value, onChange, placeholder, testid, type = "text", maxLength }) {
  return (
    <label className="block">
      <div className="fl-label mb-1 text-xs">{label}</div>
      <input className="fl-input w-full" type={type} value={value || ""} placeholder={placeholder}
             maxLength={maxLength} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </label>
  );
}

function ColorField({ label, value, onChange, placeholder, testid }) {
  const invalid = value && !isHex(value);
  const swatch = value || placeholder;
  return (
    <label className="block">
      <div className="fl-label mb-1 text-xs flex items-center justify-between">
        <span>{label}</span>
        {invalid && <span className="text-red-400 normal-case">Invalid colour</span>}
      </div>
      <div className="flex items-center gap-2">
        <span className="w-9 h-9 rounded-lg border shrink-0" style={{ background: swatch, borderColor: "var(--fl-border)" }} />
        <input className="fl-input w-full" value={value || ""} placeholder={`default ${placeholder}`}
               onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      </div>
    </label>
  );
}

function BrandThemeEditor({ settings, set, onSave, onReset, saving }) {
  const b = settings.brand, ty = settings.typography;
  const [mode, setMode] = useState("dark");
  const th = mode === "dark" ? settings.theme : settings.themeLight;
  const themeGroup = mode === "dark" ? "theme" : "themeLight";
  return (
    <div className="space-y-8" data-testid="cms-brand-theme">
      <section className="fl-card p-5">
        <h3 className="fl-heading text-lg mb-4 flex items-center gap-2"><ImageIcon size={16} className="text-[color:var(--fl-orange)]" /> Brand</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Clinic / business name" value={b.clinicName} maxLength={80}
                 onChange={(v) => set("brand", "clinicName", v)} placeholder="Functional LAB" testid="brand-clinic-name" />
          <Field label="Tagline" value={b.tagline} maxLength={120}
                 onChange={(v) => set("brand", "tagline", v)} placeholder="Movement is Medicine." testid="brand-tagline" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <ImageUploader label="Main logo (dark bg)" value={b.logoUrl} onChange={(v) => set("brand", "logoUrl", v)} testid="brand-logo" />
          <ImageUploader label="Light-theme logo (optional)" value={b.logoLightUrl} onChange={(v) => set("brand", "logoLightUrl", v)} testid="brand-logo-light" />
          <ImageUploader label="Compact / mobile mark (optional)" value={b.logoMarkUrl} onChange={(v) => set("brand", "logoMarkUrl", v)} testid="brand-logo-mark" />
          <ImageUploader label="Favicon" value={b.faviconUrl} onChange={(v) => set("brand", "faviconUrl", v)} testid="brand-favicon" />
          <ImageUploader label="Social share image (optional)" value={b.shareImageUrl} onChange={(v) => set("brand", "shareImageUrl", v)} testid="brand-share" />
        </div>
      </section>

      <section className="fl-card p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <h3 className="fl-heading text-lg flex items-center gap-2"><Palette size={16} className="text-[color:var(--fl-orange)]" /> Theme colours</h3>
          <div className="inline-flex rounded-lg overflow-hidden border" style={{ borderColor: "var(--fl-border)" }}>
            {["dark", "light"].map((m) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`theme-mode-${m}`}
                className="px-3 py-1.5 text-xs capitalize"
                style={{ background: mode === m ? "var(--fl-orange)" : "transparent", color: mode === m ? "#fff" : "var(--fl-muted)" }}>
                {m} theme
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs mb-4" style={{ color: "var(--fl-muted)" }}>Editing the <strong>{mode}</strong> public theme. Leave blank to keep the Functional LAB default.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {THEME_FIELDS.map(([k, label, def]) => (
            <ColorField key={`${mode}-${k}`} label={label} value={th[k]} placeholder={def}
                        onChange={(v) => set(themeGroup, k, v)} testid={`theme-${k}`} />
          ))}
        </div>
      </section>

      <section className="fl-card p-5">
        <h3 className="fl-heading text-lg mb-4 flex items-center gap-2"><TypeIcon size={16} className="text-[color:var(--fl-orange)]" /> Typography & shape</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <label className="block">
            <div className="fl-label mb-1 text-xs">Heading font</div>
            <select className="fl-input w-full" value={ty.headingFont} onChange={(e) => set("typography", "headingFont", e.target.value)} data-testid="type-heading">
              {FONT_OPTS.map((f) => <option key={f} value={f}>{f || "Default"}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="fl-label mb-1 text-xs">Body font</div>
            <select className="fl-input w-full" value={ty.bodyFont} onChange={(e) => set("typography", "bodyFont", e.target.value)} data-testid="type-body">
              {FONT_OPTS.map((f) => <option key={f} value={f}>{f || "Default"}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="fl-label mb-1 text-xs">Corner radius</div>
            <select className="fl-input w-full" value={ty.radius} onChange={(e) => set("typography", "radius", e.target.value)} data-testid="type-radius">
              {RADIUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        </div>
      </section>

      <SaveApplyBar onSave={onSave} onReset={onReset} saving={saving} />
    </div>
  );
}

function SocialSeoEditor({ settings, set, onSave, onReset, saving }) {
  const so = settings.social, se = settings.seo;
  return (
    <div className="space-y-8" data-testid="cms-social-seo">
      <section className="fl-card p-5">
        <h3 className="fl-heading text-lg mb-4 flex items-center gap-2"><Share2 size={16} className="text-[color:var(--fl-orange)]" /> Social links</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {["instagram", "facebook", "linkedin", "youtube", "tiktok"].map((k) => (
            <Field key={k} label={k[0].toUpperCase() + k.slice(1)} value={so[k]} maxLength={600}
                   onChange={(v) => set("social", k, v)} placeholder="https://…" testid={`social-${k}`} />
          ))}
        </div>
      </section>
      <section className="fl-card p-5">
        <h3 className="fl-heading text-lg mb-4 flex items-center gap-2"><Globe size={16} className="text-[color:var(--fl-orange)]" /> SEO defaults</h3>
        <div className="grid gap-4">
          <Field label="Browser / page title" value={se.title} maxLength={70} onChange={(v) => set("seo", "title", v)} placeholder="Functional LAB · Movement is Medicine" testid="seo-title" />
          <Field label="Meta description" value={se.description} maxLength={200} onChange={(v) => set("seo", "description", v)} placeholder="Rehabilitation that continues beyond the clinic." testid="seo-desc" />
          <Field label="Share title" value={se.shareTitle} maxLength={70} onChange={(v) => set("seo", "shareTitle", v)} placeholder="(defaults to page title)" testid="seo-share-title" />
          <Field label="Share description" value={se.shareDescription} maxLength={200} onChange={(v) => set("seo", "shareDescription", v)} placeholder="(defaults to meta description)" testid="seo-share-desc" />
        </div>
      </section>
      <SaveApplyBar onSave={onSave} onReset={onReset} saving={saving} />
    </div>
  );
}

function NavigationEditor({ settings, setSettings, onSave, onReset, saving }) {
  const nav = settings.nav;
  const byKey = {};
  (nav.items || []).forEach((it) => { byKey[it.route_key] = it; });
  const rows = NAV_KEYS.map(([key, def], i) => {
    const o = byKey[key] || {};
    return { key, def, label: o.label || "", visible: o.visible !== false, order: Number.isFinite(o.order) ? o.order : i };
  }).sort((a, b) => a.order - b.order);

  const writeItems = (next) => setSettings((s) => ({ ...s, nav: { ...s.nav, items: next } }));
  const setRow = (key, patch) => {
    const merged = rows.map((r) => (r.key === key ? { ...r, ...patch } : r))
      .map((r, idx) => ({ route_key: r.key, label: r.label, visible: r.visible, order: idx }));
    writeItems(merged);
  };
  const move = (key, dir) => {
    const idx = rows.findIndex((r) => r.key === key);
    const j = idx + dir;
    if (j < 0 || j >= rows.length) return;
    const arr = [...rows];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    writeItems(arr.map((r, i) => ({ route_key: r.key, label: r.label, visible: r.visible, order: i })));
  };
  const setCta = (which, patch) => setSettings((s) => ({ ...s, nav: { ...s.nav, [which]: { ...s.nav[which], ...patch } } }));

  return (
    <div className="space-y-8" data-testid="cms-navigation">
      <section className="fl-card p-5">
        <h3 className="fl-heading text-lg mb-1 flex items-center gap-2"><MenuIcon size={16} className="text-[color:var(--fl-orange)]" /> Public navigation</h3>
        <p className="text-xs mb-4" style={{ color: "var(--fl-muted)" }}>Rename, reorder or hide menu items. The underlying page URL never changes — hiding only removes the menu link.</p>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={r.key} className="flex items-center gap-2 p-2 rounded-lg" style={{ border: "1px solid var(--fl-border)" }} data-testid={`nav-row-${r.key}`}>
              <div className="flex flex-col">
                <button onClick={() => move(r.key, -1)} disabled={i === 0} className="disabled:opacity-30" data-testid={`nav-up-${r.key}`} aria-label="Move up"><ChevronUpIcon size={14} /></button>
                <button onClick={() => move(r.key, 1)} disabled={i === rows.length - 1} className="disabled:opacity-30" data-testid={`nav-down-${r.key}`} aria-label="Move down"><ChevronDownIcon size={14} /></button>
              </div>
              <div className="w-28 shrink-0 text-xs" style={{ color: "var(--fl-muted)" }}>{r.def}<div className="text-[10px] opacity-60">/{r.key}</div></div>
              <input className="fl-input flex-1" placeholder={r.def} value={r.label} maxLength={40}
                     onChange={(e) => setRow(r.key, { label: e.target.value })} data-testid={`nav-label-${r.key}`} />
              <label className="flex items-center gap-1 text-xs shrink-0 cursor-pointer">
                <input type="checkbox" checked={r.visible} onChange={(e) => setRow(r.key, { visible: e.target.checked })} data-testid={`nav-visible-${r.key}`} /> Show
              </label>
            </div>
          ))}
        </div>
      </section>

      <section className="fl-card p-5">
        <h3 className="fl-heading text-lg mb-4 flex items-center gap-2">Call-to-action buttons</h3>
        <div className="grid sm:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="fl-label text-xs">Sign In</div>
            <Field label="Label" value={nav.signIn.label} maxLength={40} placeholder="Sign in" onChange={(v) => setCta("signIn", { label: v })} testid="cta-signin-label" />
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={nav.signIn.visible !== false} onChange={(e) => setCta("signIn", { visible: e.target.checked })} data-testid="cta-signin-visible" /> Show Sign In (login destination is preserved)</label>
          </div>
          <div className="space-y-3">
            <div className="fl-label text-xs">Book</div>
            <Field label="Label" value={nav.book.label} maxLength={40} placeholder="Book" onChange={(v) => setCta("book", { label: v })} testid="cta-book-label" />
            <Field label="Destination (internal /path or https://…)" value={nav.book.path} maxLength={300} placeholder="/contact" onChange={(v) => setCta("book", { path: v })} testid="cta-book-path" />
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={nav.book.visible !== false} onChange={(e) => setCta("book", { visible: e.target.checked })} data-testid="cta-book-visible" /> Show Book</label>
          </div>
        </div>
      </section>

      <SaveApplyBar onSave={onSave} onReset={onReset} saving={saving} />
    </div>
  );
}

function SaveApplyBar({ onSave, onReset, saving }) {
  return (
    <div className="sticky bottom-0 -mx-1 px-1 py-3 flex flex-wrap items-center justify-between gap-3"
         style={{ background: "color-mix(in oklab, var(--fl-bg) 85%, transparent)", borderTop: "1px solid var(--fl-border)" }}>
      <button className="fl-btn-ghost text-sm" onClick={onReset} data-testid="cms-reset-defaults">
        <RotateCcw size={14} /> Restore Functional LAB defaults
      </button>
      <button className="fl-btn" onClick={onSave} disabled={saving} data-testid="cms-save-apply">
        <Save size={14} /> {saving ? "Applying…" : "Save & Apply"}
      </button>
    </div>
  );
}

// ---- Phase 2b/2c: structured public page editors ----
// Each page's section list is ordered as it renders by default. `hero:true`
// = fixed first (hide-able, not reorderable). `reorder:true` = part of the
// page's reorderable trailing block. Only fields that make sense per section.
const PAGE_REGISTRY = {
  home: {
    label: "Home", route: "/", sections: [
      { key: "hero", label: "Hero", hero: true, fields: ["eyebrow", "heading", "accentText", "subheading", "body", "ctaLabel", "ctaPath", "ctaLabel2", "ctaPath2", "mediaUrl", "videoUrl"] },
      { key: "journey", label: "Journey (Assess → Restore → Build → Perform)", reorder: true, fields: ["eyebrow", "heading", "body"], items: { count: 4, fields: ["label", "body"], label: "Stage" } },
      { key: "why", label: "Why Functional LAB", reorder: true, fields: ["eyebrow", "heading", "body", "ctaLabel", "ctaPath"], items: { count: 3, fields: ["label", "body"], label: "Point" } },
      { key: "platform", label: "Connected care platform", reorder: true, fields: ["eyebrow", "heading", "body", "mediaUrl", "videoUrl"], items: { count: 4, fields: ["label"], label: "Step" } },
      { key: "starts", label: "Choose your starting point", reorder: true, fields: ["eyebrow", "heading", "body"], items: { count: 3, fields: ["label", "body", "ctaLabel", "ctaPath"], label: "Card" } },
      { key: "support", label: "Services & support options", reorder: true, fields: ["eyebrow", "heading", "body"], items: { count: 4, fields: ["label", "body", "ctaLabel", "ctaPath"], label: "Option" } },
      { key: "clinicians", label: "Clinicians spotlight", reorder: true, fields: ["eyebrow", "heading", "body", "ctaLabel", "ctaPath", "mediaUrl"] },
      { key: "testimonials", label: "Proof / testimonials", reorder: true, fields: ["eyebrow", "heading", "body", "ctaLabel", "ctaPath"] },
      { key: "finalCta", label: "Final call-to-action", reorder: true, fields: ["heading", "body", "ctaLabel", "ctaPath", "ctaLabel2", "ctaPath2"] },
    ],
  },
  team: {
    label: "The Team", route: "/team", sections: [
      { key: "hero", label: "Hero", hero: true, fields: ["eyebrow", "heading", "body"] },
      { key: "values", label: "Values", fields: ["eyebrow", "heading"] },
      { key: "cta", label: "Call-to-action", fields: ["heading", "body", "ctaLabel"] },
    ],
  },
  services: {
    label: "Services", route: "/services", sections: [
      { key: "hero", label: "Hero", hero: true, fields: ["eyebrow", "heading", "body"] },
      { key: "pathway", label: "Pathway", fields: ["eyebrow", "heading", "body"] },
      { key: "philosophy", label: "Our philosophy", fields: ["eyebrow", "heading"] },
      { key: "cta", label: "Call-to-action", fields: ["eyebrow", "heading", "body", "ctaLabel"] },
    ],
  },
  prices: {
    label: "Prices", route: "/prices", sections: [
      { key: "hero", label: "Hero", hero: true, fields: ["eyebrow", "heading"] },
      { key: "paymentConditions", label: "Payment conditions", fields: ["eyebrow", "heading", "body"] },
      { key: "stillDeciding", label: "Still deciding (CTA)", fields: ["eyebrow", "heading", "ctaLabel"] },
    ],
  },
  finder: {
    label: "Find Your Service", route: "/service-finder", sections: [
      { key: "hero", label: "Hero", hero: true, fields: ["eyebrow", "heading", "body"] },
      { key: "reassurance", label: "Reassurance cards", fields: [] },
      { key: "waCta", label: "WhatsApp CTA", fields: [] },
    ],
  },
  resources: {
    label: "Resources", route: "/resources", sections: [
      { key: "hero", label: "Hero", hero: true, fields: ["eyebrow", "heading", "body"] },
      { key: "finalCta", label: "Final call-to-action", fields: ["heading", "body", "ctaLabel"] },
    ],
  },
  community: {
    label: "Community", route: "/community", sections: [
      { key: "hero", label: "Hero", hero: true, fields: ["eyebrow", "heading", "body"] },
      { key: "benefits", label: "Benefits", fields: ["eyebrow", "heading"] },
      { key: "groups", label: "Curated groups", fields: ["eyebrow", "heading", "body"] },
      { key: "categories", label: "Spaces directory", fields: ["eyebrow", "heading", "body"] },
      { key: "guidelines", label: "Guidelines", fields: ["eyebrow", "heading", "body"] },
      { key: "faq", label: "FAQ", fields: ["eyebrow", "heading"] },
      { key: "finalCta", label: "Final call-to-action", fields: ["eyebrow", "heading", "body"] },
    ],
  },
  testimonials: {
    label: "Testimonials", route: "/testimonials", sections: [
      { key: "hero", label: "Hero", hero: true, fields: ["eyebrow", "heading", "body"] },
      { key: "trust", label: "Trust strip", fields: ["heading"] },
      { key: "cta", label: "Call-to-action", fields: ["heading", "body", "ctaLabel"] },
    ],
  },
  contact: {
    label: "Contact", route: "/contact", sections: [
      { key: "hero", label: "Hero", hero: true, fields: ["eyebrow", "heading", "body"] },
    ],
  },
};
const FIELD_META = {
  eyebrow: { label: "Eyebrow / small label", type: "text", max: 120 },
  heading: { label: "Heading", type: "text", max: 200 },
  accentText: { label: "Accent heading (highlighted)", type: "text", max: 120 },
  subheading: { label: "Third heading line", type: "text", max: 240 },
  body: { label: "Supporting copy", type: "textarea", max: 2000 },
  ctaLabel: { label: "Primary button label", type: "text", max: 60 },
  ctaPath: { label: "Primary button link (/path or https://)", type: "text", max: 300 },
  ctaLabel2: { label: "Secondary button label", type: "text", max: 60 },
  ctaPath2: { label: "Secondary button link", type: "text", max: 300 },
  mediaUrl: { label: "Image", type: "image" },
  videoUrl: { label: "Background video URL (optional — overrides image)", type: "text", max: 600 },
};
const ITEM_FIELD_META = {
  label: { label: "Title", type: "text", max: 120 },
  body: { label: "Text", type: "textarea", max: 600 },
  ctaLabel: { label: "Button label", type: "text", max: 60 },
  ctaPath: { label: "Button link (/path or https://)", type: "text", max: 300 },
};

function PageField({ sectionKey, field, value, onChange }) {
  const meta = FIELD_META[field];
  if (!meta) return null;
  const full = meta.type === "image" || meta.type === "textarea";
  const tid = `page-${sectionKey}-${field}`;
  if (meta.type === "image") {
    return <div className="sm:col-span-2"><ImageUploader label={meta.label} value={value} onChange={onChange} testid={tid} /></div>;
  }
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <div className="fl-label mb-1 text-xs">{meta.label}</div>
      {meta.type === "textarea" ? (
        <textarea className="fl-input w-full" rows={3} maxLength={meta.max} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />
      ) : (
        <input className="fl-input w-full" maxLength={meta.max} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />
      )}
    </label>
  );
}

function PageEditor({ pageKey, settings, setSettings, onSave, onReset, saving }) {
  const registry = PAGE_REGISTRY[pageKey];
  const page = settings.pages?.[pageKey] || { sections: [], seo: {} };
  const byKey = {};
  (page.sections || []).forEach((s) => { if (s.key) byKey[s.key] = s; });

  const writePage = (next) => setSettings((s) => ({ ...s, pages: { ...(s.pages || {}), [pageKey]: next } }));
  const writeSections = (sections) => writePage({ ...page, sections });
  const setField = (key, field, value) => {
    const cur = byKey[key] || { key };
    writeSections([...(page.sections || []).filter((s) => s.key !== key), { ...cur, key, [field]: value }]);
  };
  const setSeo = (field, value) => writePage({ ...page, seo: { ...(page.seo || {}), [field]: value } });
  const setItemField = (key, idx, field, value) => {
    const cur = byKey[key] || { key };
    const items = [...(cur.items || [])];
    while (items.length <= idx) items.push({});
    items[idx] = { ...items[idx], [field]: value };
    writeSections([...(page.sections || []).filter((s) => s.key !== key), { ...cur, key, items }]);
  };

  const hero = registry.sections.find((r) => r.hero);
  const reorderRegs = registry.sections.filter((r) => r.reorder);
  const inlineRegs = registry.sections.filter((r) => !r.hero && !r.reorder);
  const orderedReorder = reorderRegs
    .map((reg, i) => ({ reg, order: Number.isFinite(byKey[reg.key]?.order) ? byKey[reg.key].order : i }))
    .sort((a, b) => a.order - b.order)
    .map((o) => o.reg);

  const move = (key, dir) => {
    const keys = orderedReorder.map((r) => r.key);
    const idx = keys.indexOf(key);
    const j = idx + dir;
    if (j < 0 || j >= keys.length) return;
    [keys[idx], keys[j]] = [keys[j], keys[idx]];
    const bk = {};
    (page.sections || []).forEach((s) => { bk[s.key] = s; });
    keys.forEach((k, i2) => { bk[k] = { ...(bk[k] || { key: k }), key: k, order: i2 }; });
    writeSections(Object.values(bk));
  };

  const SectionCard = ({ reg, canReorder, index }) => {
    const row = byKey[reg.key] || {};
    const visible = row.visible !== false;
    return (
      <section className="fl-card p-5" data-testid={`page-section-card-${pageKey}-${reg.key}`}>
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            {canReorder ? (
              <div className="flex flex-col">
                <button onClick={() => move(reg.key, -1)} disabled={index === 0} className="disabled:opacity-30" aria-label="Move up" data-testid={`page-up-${pageKey}-${reg.key}`}><ChevronUpIcon size={14} /></button>
                <button onClick={() => move(reg.key, 1)} disabled={index === orderedReorder.length - 1} className="disabled:opacity-30" aria-label="Move down" data-testid={`page-down-${pageKey}-${reg.key}`}><ChevronDownIcon size={14} /></button>
              </div>
            ) : <GripVertical size={14} style={{ color: "var(--fl-muted-2)" }} />}
            <h3 className="fl-heading text-lg">{reg.label}</h3>
          </div>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            {visible ? <Eye size={14} /> : <EyeOff size={14} />}
            <input type="checkbox" checked={visible} onChange={(e) => setField(reg.key, "visible", e.target.checked)} data-testid={`page-visible-${pageKey}-${reg.key}`} /> Show on page
          </label>
        </div>
        {reg.fields.length > 0 ? (
          <div className="grid sm:grid-cols-2 gap-4" style={{ opacity: visible ? 1 : 0.55 }}>
            {reg.fields.map((f) => (
              <PageField key={f} sectionKey={`${pageKey}-${reg.key}`} field={f} value={row[f]} onChange={(v) => setField(reg.key, f, v)} />
            ))}
          </div>
        ) : (
          <p className="text-xs" style={{ color: "var(--fl-muted-2)" }}>This section has no editable text — use the Show toggle to show or hide it.</p>
        )}
        {reg.items && (
          <div className="mt-5 pt-4 border-t" style={{ borderColor: "var(--fl-border)" }}>
            <div className="fl-label mb-3 text-xs">{reg.label} items — leave blank to keep the default</div>
            <div className="space-y-3">
              {Array.from({ length: reg.items.count }).map((_, idx) => {
                const itm = (row.items || [])[idx] || {};
                return (
                  <div key={idx} className="rounded-md border p-3" style={{ borderColor: "var(--fl-border)" }} data-testid={`page-item-${pageKey}-${reg.key}-${idx}`}>
                    <div className="fl-label text-[10px] mb-2" style={{ color: "var(--fl-muted-2)" }}>{reg.items.label} {idx + 1}</div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {reg.items.fields.map((f) => {
                        const meta = ITEM_FIELD_META[f] || { label: f, type: "text", max: 200 };
                        const tid = `page-item-${pageKey}-${reg.key}-${idx}-${f}`;
                        const full = meta.type === "textarea";
                        return (
                          <label key={f} className={`block ${full ? "sm:col-span-2" : ""}`}>
                            <div className="fl-label mb-1 text-[11px]">{meta.label}</div>
                            {meta.type === "textarea" ? (
                              <textarea className="fl-input w-full" rows={2} maxLength={meta.max} value={itm[f] || ""} onChange={(e) => setItemField(reg.key, idx, f, e.target.value)} data-testid={tid} />
                            ) : (
                              <input className="fl-input w-full" maxLength={meta.max} value={itm[f] || ""} onChange={(e) => setItemField(reg.key, idx, f, e.target.value)} data-testid={tid} />
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="space-y-6" data-testid={`cms-page-editor-${pageKey}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs max-w-2xl" style={{ color: "var(--fl-muted)" }}>
          Edit the <strong>{registry.label}</strong> page content. Leave a field blank to keep the Functional LAB default. Hiding a section only stops it rendering — nothing is deleted. Services, packages, team, resources, community, testimonials and contact details stay managed in their own areas. Changes go live on <strong>Save &amp; Apply</strong>.
        </p>
        <a href={registry.route} target="_blank" rel="noopener noreferrer" className="fl-btn-ghost text-xs whitespace-nowrap" data-testid={`page-preview-link-${pageKey}`}>
          <Eye size={14} /> Preview as visitor ↗
        </a>
      </div>

      {hero && <SectionCard reg={hero} canReorder={false} index={-1} />}
      {inlineRegs.map((reg) => <SectionCard key={reg.key} reg={reg} canReorder={false} index={-1} />)}
      {orderedReorder.map((reg, i) => <SectionCard key={reg.key} reg={reg} canReorder index={i} />)}

      <section className="fl-card p-5" data-testid={`page-seo-card-${pageKey}`}>
        <h3 className="fl-heading text-lg mb-1 flex items-center gap-2"><Globe size={16} className="text-[color:var(--fl-orange)]" /> {registry.label} page SEO</h3>
        <p className="text-xs mb-4" style={{ color: "var(--fl-muted)" }}>Overrides the global SEO defaults for this page only. Blank fields fall back to the global SEO, then the built-in default.</p>
        <div className="grid gap-4">
          <Field label="Browser / page title" value={page.seo?.title} maxLength={70} onChange={(v) => setSeo("title", v)} placeholder="(falls back to global SEO)" testid={`page-seo-title-${pageKey}`} />
          <Field label="Meta description" value={page.seo?.description} maxLength={200} onChange={(v) => setSeo("description", v)} placeholder="(falls back to global SEO)" testid={`page-seo-desc-${pageKey}`} />
          <Field label="Share title" value={page.seo?.shareTitle} maxLength={70} onChange={(v) => setSeo("shareTitle", v)} placeholder="(defaults to page title)" testid={`page-seo-share-title-${pageKey}`} />
          <Field label="Share description" value={page.seo?.shareDescription} maxLength={200} onChange={(v) => setSeo("shareDescription", v)} placeholder="(defaults to meta description)" testid={`page-seo-share-desc-${pageKey}`} />
          <ImageUploader label="Share / social image (optional)" value={page.seo?.shareImageUrl} onChange={(v) => setSeo("shareImageUrl", v)} testid={`page-seo-share-image-${pageKey}`} />
        </div>
      </section>

      <SaveApplyBar onSave={onSave} onReset={onReset} saving={saving} />
    </div>
  );
}

export default function AdminCms() {
  const [tab, setTab] = useState("brand");
  const [pageTab, setPageTab] = useState("home");
  const [settings, setSettings] = useState(emptySettings());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/public/site-settings").then((r) => {
      const d = r.data || {};
      setSettings((s) => ({
        brand: { ...s.brand, ...(d.brand || {}) },
        theme: { ...s.theme, ...(d.theme || {}) },
        themeLight: { ...s.themeLight, ...(d.themeLight || {}) },
        typography: { ...s.typography, ...(d.typography || {}) },
        social: { ...s.social, ...(d.social || {}) },
        seo: { ...s.seo, ...(d.seo || {}) },
        nav: { ...emptySettings().nav, ...(d.nav || {}) },
        pages: d.pages || {},
      }));
    }).catch(() => {});
  }, []);

  const set = (group, key, value) => setSettings((s) => ({ ...s, [group]: { ...s[group], [key]: value } }));

  const saveApply = async () => {
    // Block obviously invalid colours before applying to the public site.
    const bad = [...Object.entries(settings.theme), ...Object.entries(settings.themeLight)].find(([, v]) => v && !isHex(v));
    if (bad) { toast.error(`Invalid colour for ${bad[0]} — use #hex or rgba().`); return; }
    if (!window.confirm("Save & Apply these branding/theme changes to the public website now?")) return;
    setSaving(true);
    try {
      await api.post("/admin/site-settings", settings);
      toast.success("Branding applied to the public site");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Restore the original Functional LAB branding & theme? This only resets branding — your Team, Resources, Communities, services and contact details are NOT deleted.")) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/site-settings/reset", {});
      setSettings({
        brand: { ...emptySettings().brand, ...(data.brand || {}) },
        theme: { ...emptySettings().theme, ...(data.theme || {}) },
        themeLight: { ...emptySettings().themeLight, ...(data.themeLight || {}) },
        typography: { ...emptySettings().typography, ...(data.typography || {}) },
        social: { ...emptySettings().social, ...(data.social || {}) },
        seo: { ...emptySettings().seo, ...(data.seo || {}) },
        nav: { ...emptySettings().nav, ...(data.nav || {}) },
        pages: data.pages || {},
      });
      toast.success("Functional LAB defaults restored");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setSaving(false); }
  };

  const topTabs = [
    { id: "brand", label: "Brand & Theme", icon: Palette },
    { id: "social", label: "Footer & SEO", icon: Share2 },
    { id: "pages", label: "Pages", icon: LayoutGrid },
    { id: "nav", label: "Navigation", icon: MenuIcon },
  ];

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="fl-label text-[#f47a20]">Public site CMS</div>
        <h1 className="fl-heading text-4xl mt-2">Public website</h1>
        <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--fl-muted)" }}>
          Manage the public Functional LAB website — brand identity, theme, footer, SEO and content. Branding/theme changes only go live when you press <strong>Save &amp; Apply</strong>.
        </p>
      </div>

      <div className="flex gap-1 mb-6 border-b overflow-x-auto no-scrollbar" style={{ borderColor: "var(--fl-border)" }}>
        {topTabs.map((tt) => {
          const active = tab === tt.id; const Icon = tt.icon;
          return (
            <button key={tt.id} onClick={() => setTab(tt.id)}
              className="flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors whitespace-nowrap"
              style={{ borderColor: active ? "var(--fl-orange)" : "transparent", color: active ? "var(--fl-text)" : "var(--fl-muted)" }}
              data-testid={`cms-tab-${tt.id}`}>
              <Icon size={14} /> {tt.label}
            </button>
          );
        })}
      </div>

      {tab === "brand" && (
        <BrandThemeEditor settings={settings} set={set} onSave={saveApply} onReset={resetDefaults} saving={saving} />
      )}

      {tab === "social" && (
        <SocialSeoEditor settings={settings} set={set} onSave={saveApply} onReset={resetDefaults} saving={saving} />
      )}

      {tab === "nav" && (
        <NavigationEditor settings={settings} setSettings={setSettings} onSave={saveApply} onReset={resetDefaults} saving={saving} />
      )}

      {tab === "pages" && (
        <>
          <div className="flex gap-1 mb-6 flex-wrap" style={{ borderColor: "var(--fl-border)" }}>
            {Object.entries(PAGE_REGISTRY).map(([pk, reg]) => {
              const active = pageTab === pk;
              return (
                <button key={pk} onClick={() => setPageTab(pk)}
                  className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors"
                  style={{ background: active ? "color-mix(in oklab, var(--fl-orange) 12%, transparent)" : "transparent", color: active ? "var(--fl-orange)" : "var(--fl-muted)" }}
                  data-testid={`cms-page-tab-${pk}`}>
                  <LayoutGrid size={14} /> {reg.label}
                </button>
              );
            })}
          </div>

          <PageEditor pageKey={pageTab} settings={settings} setSettings={setSettings} onSave={saveApply} onReset={resetDefaults} saving={saving} />

          {(pageTab === "team" || pageTab === "resources" || pageTab === "community") && (
            <div className="mt-10 pt-8 border-t" style={{ borderColor: "var(--fl-border)" }}>
              <p className="text-xs mb-4" style={{ color: "var(--fl-muted)" }}>
                The records below power this page and remain the source of truth — the page content above only controls headings, intro copy, section visibility and order.
              </p>
              {pageTab === "team" && (
                <CmsSection kind="team member" reorderKind="team" icon={Users} title="Team members" endpoint="/admin/cms/team" Form={TeamForm} testidPrefix="cms-team"
                  columns={{ primary: (it) => it.name, secondary: (it) => it.role, body: (it) => it.bio }} />
              )}
              {pageTab === "resources" && (
                <CmsSection kind="resource" reorderKind="resources" icon={BookOpen} title="Resources" endpoint="/admin/cms/resources" Form={ResourceForm} testidPrefix="cms-res"
                  columns={{ primary: (it) => it.title, secondary: (it) => it.category, body: (it) => it.excerpt }} />
              )}
              {pageTab === "community" && (
                <CmsSection kind="community" reorderKind="communities" icon={MessageSquare} title="Communities" endpoint="/admin/cms/communities" Form={CommunityForm} testidPrefix="cms-com"
                  columns={{ primary: (it) => it.name, secondary: (it) => `${it.member_count || 0} members${it.tags?.length ? ` · ${it.tags.join(", ")}` : ""}`, body: (it) => it.description }} />
              )}
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}

const STATUS_META = {
  draft:       { label: "Draft",       icon: EyeOff, color: "#8A8A8A" },
  published:   { label: "Published",   icon: Eye,    color: "#22C55E" },
  coming_soon: { label: "Coming Soon", icon: Clock,  color: "#f47a20" },
  archived:    { label: "Archived",    icon: Archive,color: "#6B7280" },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.draft;
  const Icon = meta.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold"
      style={{
        color: meta.color,
        background: `color-mix(in oklab, ${meta.color} 14%, transparent)`,
        border: `1px solid color-mix(in oklab, ${meta.color} 30%, transparent)`,
      }}
      data-testid={`cms-status-${status}`}
    >
      <Icon size={10} /> {meta.label}
    </span>
  );
}

// Resolve a stored photo/cover URL. CMS-managed uploads come back as a
// relative path like "/api/public/cms/media/{id}"; older absolute URLs
// (Unsplash / customer assets) are returned as-is.
function resolveMediaUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/api/")) return `${API}${url.slice(4)}`; // API already ends with /api
  return url;
}

function ImageUploader({ value, onChange, label = "Image", testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const preview = resolveMediaUrl(value);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("fl_token");
      const { data } = await axios.post(`${API}/admin/cms/upload-image`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="fl-label" style={{ color: "var(--fl-muted-2)" }}>{label}</div>
      <div className="flex items-center gap-3">
        <div
          className="w-20 h-20 rounded-md border grid place-items-center overflow-hidden shrink-0"
          style={{ borderColor: "var(--fl-border)", background: "color-mix(in oklab, var(--fl-orange) 4%, transparent)" }}
        >
          {preview ? (
            <img src={preview} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={20} style={{ color: "var(--fl-muted-2)" }} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid={testid}
          />
          <div className="flex flex-wrap gap-2">
            <button type="button" className="fl-btn-ghost text-xs" onClick={() => inputRef.current?.click()} disabled={uploading}>
              <Upload size={12} /> {uploading ? "Uploading…" : "Upload"}
            </button>
            {value && (
              <button type="button" className="fl-btn-ghost text-xs text-red-400" onClick={() => onChange("")}>
                Remove
              </button>
            )}
          </div>
          <div className="text-[10px] mt-1 truncate" style={{ color: "var(--fl-muted-2)" }}>{value || "No image"}</div>
        </div>
      </div>
    </div>
  );
}

// Bind a datetime-local input to an ISO string. Empty string ↔ null.
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  // datetime-local expects "YYYY-MM-DDTHH:mm" in local tz
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInput(local) {
  if (!local) return null;
  const d = new Date(local);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

function PublishSchedule({ value, status, onChange, testid }) {
  // Only meaningful for Draft rows — otherwise show as read-only info.
  const local = toLocalInput(value);
  const isRelevant = status === "draft";
  return (
    <div className="space-y-2" data-testid={`${testid}-wrap`}>
      <div className="fl-label flex items-center gap-2" style={{ color: "var(--fl-muted-2)" }}>
        <CalendarClock size={12} /> Auto-publish on
      </div>
      <input
        type="datetime-local"
        className="fl-input w-full text-sm"
        value={local}
        onChange={(e) => onChange(fromLocalInput(e.target.value))}
        disabled={!isRelevant}
        data-testid={testid}
      />
      <div className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>
        {isRelevant
          ? "Draft will automatically flip to Published at this time. Leave empty for manual publishing."
          : "Only applies to Draft rows. Move status back to Draft to schedule."}
      </div>
    </div>
  );
}

function CmsModal({ open, onClose, title, children }) {
  const ref = useModalA11y({ open, onClose });
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cms-modal-title"
        className="fl-card-elevated w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6"
        style={{ background: "var(--fl-bg-2)" }}
      >
        <div className="flex justify-between items-center mb-5">
          <h3 id="cms-modal-title" className="fl-heading text-2xl">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="text-zinc-400 hover:text-white"><X size={22} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ----- Team form -----
function TeamForm({ initial, onSubmit, onCancel }) {
  const [f, setF] = useState(initial || { name: "", role: "Sport Rehabilitator", bio: "", photo_url: "", status: "draft", order: 999, publish_at: null });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-3" data-testid="cms-team-form">
      <input required placeholder="Name" className="fl-input w-full" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cms-team-name" />
      <input placeholder="Role" className="fl-input w-full" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="cms-team-role" />
      <ImageUploader value={f.photo_url} onChange={(url) => setF({ ...f, photo_url: url })} label="Photo" testid="cms-team-photo-file" />
      <textarea rows={4} placeholder="Bio" className="fl-input w-full" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} data-testid="cms-team-bio" />
      <div className="grid grid-cols-2 gap-3">
        <select className="fl-input w-full" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="cms-team-status">
          {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
        </select>
        <div className="text-[11px] flex items-center" style={{ color: "var(--fl-muted-2)" }}>
          Position: drag rows in the list to reorder.
        </div>
      </div>
      <PublishSchedule value={f.publish_at} status={f.status} onChange={(v) => setF({ ...f, publish_at: v })} testid="cms-team-publish-at" />
      <div className="flex gap-2 justify-end pt-3">
        <button type="button" onClick={onCancel} className="fl-btn-ghost">Cancel</button>
        <button type="submit" className="fl-btn" data-testid="cms-team-save"><Save size={14} /> Save</button>
      </div>
    </form>
  );
}

// ----- Resource form -----
function ResourceForm({ initial, onSubmit, onCancel }) {
  const [f, setF] = useState(initial || { title: "", excerpt: "", category: "general", cover_image_url: "", body_md: "", download_url: "", status: "draft", order: 999, publish_at: null });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="space-y-3" data-testid="cms-res-form">
      <input required placeholder="Title" className="fl-input w-full" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="cms-res-title" />
      <textarea rows={2} placeholder="Excerpt" className="fl-input w-full" value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} data-testid="cms-res-excerpt" />
      <div className="grid grid-cols-2 gap-3">
        <input placeholder="Category" className="fl-input w-full" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
        <input placeholder="Download URL (PDF, etc.)" className="fl-input w-full" value={f.download_url} onChange={(e) => setF({ ...f, download_url: e.target.value })} data-testid="cms-res-download" />
      </div>
      <ImageUploader value={f.cover_image_url} onChange={(url) => setF({ ...f, cover_image_url: url })} label="Cover image" testid="cms-res-cover-file" />
      <textarea rows={6} placeholder="Body (Markdown)" className="fl-input w-full font-mono text-xs" value={f.body_md} onChange={(e) => setF({ ...f, body_md: e.target.value })} />
      <div className="grid grid-cols-2 gap-3">
        <select className="fl-input w-full" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="cms-res-status">
          {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
        </select>
        <div className="text-[11px] flex items-center" style={{ color: "var(--fl-muted-2)" }}>
          Position: drag rows in the list to reorder.
        </div>
      </div>
      <PublishSchedule value={f.publish_at} status={f.status} onChange={(v) => setF({ ...f, publish_at: v })} testid="cms-res-publish-at" />
      <div className="flex gap-2 justify-end pt-3">
        <button type="button" onClick={onCancel} className="fl-btn-ghost">Cancel</button>
        <button type="submit" className="fl-btn" data-testid="cms-res-save"><Save size={14} /> Save</button>
      </div>
    </form>
  );
}

// ----- Community form -----
function CommunityForm({ initial, onSubmit, onCancel }) {
  const [f, setF] = useState(initial || { name: "", description: "", cover_image_url: "", external_url: "", member_count: 0, tags: [], status: "draft", order: 999, publish_at: null });
  const [tagsText, setTagsText] = useState((initial?.tags || []).join(", "));
  const save = () => onSubmit({ ...f, tags: tagsText.split(",").map((s) => s.trim()).filter(Boolean) });
  return (
    <form onSubmit={(e) => { e.preventDefault(); save(); }} className="space-y-3" data-testid="cms-com-form">
      <input required placeholder="Name" className="fl-input w-full" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cms-com-name" />
      <textarea rows={3} placeholder="Description" className="fl-input w-full" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="cms-com-desc" />
      <ImageUploader value={f.cover_image_url} onChange={(url) => setF({ ...f, cover_image_url: url })} label="Cover image" testid="cms-com-cover-file" />
      <input placeholder="External URL (e.g. Discord, WhatsApp)" className="fl-input w-full" value={f.external_url} onChange={(e) => setF({ ...f, external_url: e.target.value })} data-testid="cms-com-url" />
      <div className="grid grid-cols-2 gap-3">
        <input type="number" min="0" placeholder="Member count" className="fl-input w-full" value={f.member_count} onChange={(e) => setF({ ...f, member_count: parseInt(e.target.value) || 0 })} data-testid="cms-com-members" />
        <input placeholder="Tags (comma-separated)" className="fl-input w-full" value={tagsText} onChange={(e) => setTagsText(e.target.value)} data-testid="cms-com-tags" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <select className="fl-input w-full" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="cms-com-status">
          {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
        </select>
        <div className="text-[11px] flex items-center" style={{ color: "var(--fl-muted-2)" }}>
          Position: drag rows in the list to reorder.
        </div>
      </div>
      <PublishSchedule value={f.publish_at} status={f.status} onChange={(v) => setF({ ...f, publish_at: v })} testid="cms-com-publish-at" />
      <div className="flex gap-2 justify-end pt-3">
        <button type="button" onClick={onCancel} className="fl-btn-ghost">Cancel</button>
        <button type="submit" className="fl-btn" data-testid="cms-com-save"><Save size={14} /> Save</button>
      </div>
    </form>
  );
}

// ----- Section (generic table for each CMS resource type) -----
function CmsSection({ kind, reorderKind, icon: Icon, title, endpoint, Form, columns, testidPrefix }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { mode: 'create' | 'edit', item?: {...} }
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const refresh = () => api.get(endpoint).then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const save = async (data) => {
    try {
      if (modal.mode === "create") {
        await api.post(endpoint, data);
        toast.success(`${title} created`);
      } else {
        await api.patch(`${endpoint}/${modal.item.id}`, data);
        toast.success(`${title} updated`);
      }
      setModal(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!confirm(`Delete this ${kind}?`)) return;
    try {
      await api.delete(`${endpoint}/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  // Drag & drop reorder — HTML5 native, minimal.
  const onDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    // Firefox needs some data to fire dragend properly.
    try { e.dataTransfer.setData("text/plain", id); } catch { /* noop */ }
  };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    if (id !== overId) setOverId(id);
  };
  const onDragEnd = () => { setDragId(null); setOverId(null); };
  const onDrop = (targetId) => async (e) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { setDragId(null); setOverId(null); return; }
    const from = items.findIndex((x) => x.id === dragId);
    const to = items.findIndex((x) => x.id === targetId);
    if (from < 0 || to < 0) return;
    const reordered = [...items];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setItems(reordered);
    setDragId(null); setOverId(null);
    setSavingOrder(true);
    try {
      const { data } = await api.post(`/admin/cms/${reorderKind}/reorder`, {
        ids: reordered.map((x) => x.id),
      });
      setItems(data);
      toast.success("Order saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reorder failed");
      refresh();
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <div className="mb-12" data-testid={`${testidPrefix}-section`}>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <Icon size={20} className="text-[color:var(--fl-orange)]" />
          <h2 className="fl-heading text-2xl">{title}</h2>
          <span className="text-xs" style={{ color: "var(--fl-muted-2)" }}>{items.length} items</span>
          {savingOrder && <span className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>Saving order…</span>}
        </div>
        <button className="fl-btn text-sm" onClick={() => setModal({ mode: "create" })} data-testid={`${testidPrefix}-new`}>
          <Plus size={14} /> New
        </button>
      </div>

      {loading ? (
        <div className="fl-card p-6 text-sm" style={{ color: "var(--fl-muted)" }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="fl-card p-6 text-sm" style={{ color: "var(--fl-muted)" }}>No items yet.</div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const isDragging = dragId === it.id;
            const isOver = overId === it.id && dragId && dragId !== it.id;
            return (
              <div
                key={it.id}
                onDragOver={onDragOver(it.id)}
                onDrop={onDrop(it.id)}
                onDragEnd={onDragEnd}
                className="fl-card-elevated p-4 flex items-start justify-between gap-4 transition-all"
                style={{
                  opacity: isDragging ? 0.4 : 1,
                  transform: isOver ? "translateY(-2px)" : undefined,
                  boxShadow: isOver ? "0 0 0 2px var(--fl-orange)" : undefined,
                }}
                data-testid={`${testidPrefix}-row-${it.id}`}
              >
                <button
                  type="button"
                  draggable
                  onDragStart={onDragStart(it.id)}
                  onDragEnd={onDragEnd}
                  className="shrink-0 mt-1 cursor-grab active:cursor-grabbing p-1 rounded hover:bg-white/5"
                  aria-label="Drag to reorder"
                  data-testid={`${testidPrefix}-drag-${it.id}`}
                  title="Drag to reorder"
                >
                  <GripVertical size={16} style={{ color: "var(--fl-muted-2)" }} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="fl-heading text-lg">{columns.primary(it)}</div>
                    <StatusBadge status={it.status} />
                    {it.status === "draft" && it.publish_at && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider"
                        style={{ color: "#F5B700", background: "color-mix(in oklab, #F5B700 14%, transparent)", border: "1px solid color-mix(in oklab, #F5B700 30%, transparent)" }}
                        data-testid={`${testidPrefix}-scheduled-${it.id}`}
                      >
                        <CalendarClock size={10} /> Scheduled {new Date(it.publish_at).toLocaleString()}
                      </span>
                    )}
                    {columns.secondary && <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>{columns.secondary(it)}</div>}
                  </div>
                  {columns.body && <p className="text-sm mt-1 line-clamp-2" style={{ color: "var(--fl-muted)" }}>{columns.body(it)}</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button className="fl-btn-ghost text-xs px-3 py-1.5" onClick={() => setModal({ mode: "edit", item: it })} data-testid={`${testidPrefix}-edit-${it.id}`}>Edit</button>
                  <button className="fl-btn-ghost text-xs px-3 py-1.5 text-red-400" onClick={() => remove(it.id)} data-testid={`${testidPrefix}-delete-${it.id}`}><Trash2 size={12} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CmsModal
        open={!!modal}
        onClose={() => setModal(null)}
        title={modal?.mode === "edit" ? `Edit ${title.replace(/s$/, "")}` : `New ${title.replace(/s$/, "")}`}
      >
        {modal && <Form initial={modal.item} onSubmit={save} onCancel={() => setModal(null)} />}
      </CmsModal>
    </div>
  );
}

